import React from "react";
import "./ExhibitorCard.css";
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { DEFAULT_EX_ICON } from "../constants/constants";
import InteractiveAction2 from "./InteractiveAction2";

const ExhibitorCard = ({ item, isOneline = false, viewMode = "grid" }) => {
  // Check if the exhibitor has uploaded a custom image
  const hasCustomImage = Boolean(item?.profileImage);

  if (viewMode === "list") {
    return (
      <Card
        elevation={0}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          p: 2,
          border: "1px solid #eef2f6",
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
            borderColor: "#cbd5e1",
          },
        }}
      >
        <Badge
          color="success"
          variant="dot"
          invisible={!item.isOnline}
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Avatar
            src={hasCustomImage ? item.profileImage : DEFAULT_EX_ICON}
            alt={`${item?.companyName?.value}`}
            sx={{
              width: 70,
              height: 70,
              // Dark background for the default white icon, light for custom uploads
              backgroundColor: hasCustomImage ? "#f8fafc" : "#334155",
              border: "3px solid #fff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              "& img": {
                objectFit: "contain",
                padding: hasCustomImage ? 0 : "12px", // Add padding so the default icon doesn't touch the edges
              },
            }}
          />
        </Badge>
        <Box
          sx={{
            flexGrow: 1,
            ml: { xs: 0, sm: 3 },
            mt: { xs: 2, sm: 0 },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#1e293b", fontSize: "1.1rem" }}
          >
            {item?.companyName?.value}
          </Typography>
          <Link
            to={`/hall-view/${item?.hallNumber?.value}?un=${item.username}`}
            style={{ textDecoration: "none" }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                mt: 1,
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "16px",
              }}
            >
              Hall {item?.hallNumber?.value} | Stand {item?.standNumber?.value}
            </Typography>
          </Link>
        </Box>
        <Box sx={{ mt: { xs: 2, sm: 0 } }}>
          <InteractiveAction2 item={item} />
        </Box>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        maxWidth: 300,
        height: isOneline ? undefined : 280,
        border: "1px solid #eef2f6",
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "all 0.3s ease-in-out",
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
          borderColor: "#cbd5e1",
        },
      }}
    >
      <CardContent sx={{ textAlign: "center", flexGrow: 1 }}>
        <Badge
          color="success"
          variant="dot"
          invisible={!item.isOnline} // Assuming there's a flag for online status
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Avatar
            src={hasCustomImage ? item.profileImage : DEFAULT_EX_ICON}
            alt={`${item?.companyName?.value}`}
            sx={{
              width: 90,
              height: 90,
              margin: "auto",
              // Dark background for the default white icon, light for custom uploads
              backgroundColor: hasCustomImage ? "#f8fafc" : "#334155",
              border: "3px solid #fff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.05)" },
              "& img": {
                objectFit: "contain",
                padding: hasCustomImage ? 0 : "16px", // Slightly more padding for the larger 90x90 avatar
              },
            }}
          />
        </Badge>
        <Box sx={{ mt: 2 }}>
          <Box height={isOneline ? undefined : "48px"}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "#1e293b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: isOneline ? 1 : 2, // Set line clamp based on isOneline
                lineHeight: "1.2em",
                maxHeight: isOneline ? "1.2em" : "2.4em", // Adjust maxHeight for one or two lines
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" }, // Responsive font size
              }}
            >
              {item?.companyName?.value}
            </Typography>
          </Box>

          <Link
            to={`/hall-view/${item?.hallNumber?.value}?un=${item.username}`}
            style={{ textDecoration: "none" }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                mt: isOneline ? 2 : 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "16px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  color: "#0f172a",
                },
              }}
            >
              {item?.hallNumber?.value} | {item?.standNumber?.value}
            </Typography>
          </Link>
        </Box>
        <Box mt={2}></Box>
        <InteractiveAction2 item={item} />
      </CardContent>
    </Card>
  );
};

export default ExhibitorCard;
